import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabDirective, TabsModule } from 'ngx-bootstrap/tabs';
import { BadgeModule } from '@coreui/angular';
import { User } from '../../models/users/user.model';
import { UserCurrencyType } from '../../enums/users/user-currency-type.enum';
import { UsersFiltersRequest } from '../../models/users/requests/api-users-get-users-request.model';
import { DateService } from '../../services/date.service';
import { ApiUsersService } from '../../services/users/api/api-users.service';
import { AlertsService } from '../../services/alerts.service';
import { ModalsService } from '../../services/modals.service';
import { storageConfig } from '../../config/storage.config';
import { UsersResultsResponse } from '../../models/users/responses/api-users-results-response.model';
import { UserAddCurrencyModalComponent } from './modals/user-add-currency-modal/user-add-currency-modal.component';
import { UserAddExperienceModalComponent } from './modals/user-add-experience-modal/user-add-experience-modal.component';
import { ConfigurationsBlockComponent } from '../configurations/configurations-block/configurations-block.component';
import { ConfigurationDef } from '../configurations/configurations-block/configuration-def.model';
import { UsersStatisticsTabComponent } from './tabs/users-statistics-tab/users-statistics-tab.component';

@Component({
  templateUrl: 'users.component.html',
    standalone: true,
    imports: [CollapseModule, FormsModule, RouterModule, CommonModule, BadgeModule, TooltipModule, TabDirective, TabsModule, UserAddCurrencyModalComponent, UserAddExperienceModalComponent, ConfigurationsBlockComponent, UsersStatisticsTabComponent]
})
export class UsersComponent implements OnInit {

  protected readonly UserCurrencyType = UserCurrencyType;

  readonly usersConfigurations: ConfigurationDef[] = [
    { name: 'game_user_displayname_letters_min', label: 'Display Name Letters Min', type: 'number' },
    { name: 'game_user_displayname_letters_max', label: 'Display Name Letters Max', type: 'number' },
    { name: 'game_user_delete_cancel_timeout', label: 'Delete Cancel Timeout', type: 'number' },
    { name: 'game_user_clean_timeout', label: 'Clean Timeout', type: 'number' },
    { name: 'game_currency_initial_credits_quantity', label: 'Initial Credits Quantity', type: 'number' },
    { name: 'game_currency_free_credits_base_quantity', label: 'Free Credits Base Quantity', type: 'number' },
    { name: 'game_currency_free_credits_time', label: 'Free Credits Time', type: 'number' },
    { name: 'user_displayname_forbidden_words', label: 'Displayname Forbidden Words', type: 'string' },
  ];

  @ViewChild('addCurrencyModal') public addCurrencyModal: UserAddCurrencyModalComponent;
  @ViewChild('addExperienceModal') public addExperienceModal: UserAddExperienceModalComponent;
  @ViewChild('statisticsTab') statisticsTab!: UsersStatisticsTabComponent;

  onSelectStatisticsTab(): void { this.statisticsTab.load(); }

  selectedUserId: number = 0;

  isLoading: boolean = false;

  usersOnlineCount: number;

  users: User[] = [];
  resultsPerPage: number = 10;

  totalResults: number;

  private static pageNumber: number = 1;

  get pageNumber() {
    return UsersComponent.pageNumber;
  }

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;

    const visibleSet = new Set<number>();

    // Always show first 2 and last 2
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);

    // Show 2 around current
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);

    const sorted = Array.from(visibleSet).sort((a, b) => a - b);

    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }

    return result;
  }

  goToPage(page: number): void {
    UsersComponent.pageNumber = page;
    this.refreshUsersList();
  }

  // Filters
  isFilterOpen: boolean = false;
  requestFilters: UsersFiltersRequest = new UsersFiltersRequest();

  constructor (public dateService: DateService, private apiUsersService: ApiUsersService, private alertsService: AlertsService, private route: ActivatedRoute, private modalsService: ModalsService) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const isBack: boolean = params['isBack'];

      this.retrieveFiltersFromStorage();

      this.refreshUsersList();
    });
  }

  onClickFilter() {
    this.saveFilters();
    this.refreshUsersList();
  }

  onClickResetFilters() {
    this.requestFilters = new UsersFiltersRequest();

    this.saveFilters();
    this.refreshUsersList();
  }

  changeFilterOpenState() {
    this.isFilterOpen = !this.isFilterOpen;
  }

  onClickPaginationPrevious() {
    UsersComponent.pageNumber--;
    this.refreshUsersList();
  }

  onClickPaginationNext() {
    UsersComponent.pageNumber++;
    this.refreshUsersList();
  }

  private refreshUsersList() {
    this.isLoading = true;

    this.apiUsersService.getUsers(UsersComponent.pageNumber, this.resultsPerPage, this.requestFilters).then(
      (response: UsersResultsResponse) => {
        this.isLoading = false;
        this.users = response.results;

        this.saveFilters();

        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;

        this.alertsService.displayAlertError("Error during user display ("+error+")");
      }
    );
  }

  private retrieveFiltersFromStorage() {
    const requestFilters: UsersFiltersRequest = JSON.parse(localStorage.getItem(storageConfig.usersFilters) ?? '{}');

    if (requestFilters) {
      this.requestFilters = requestFilters;
    }
  }

  onClickAddCurrency(userId: number): void {
    this.selectedUserId = userId;
    setTimeout(() => this.addCurrencyModal.show(() => this.refreshUsersList()));
  }

  onClickAddExperience(userId: number): void {
    this.selectedUserId = userId;
    setTimeout(() => this.addExperienceModal.show(() => this.refreshUsersList()));
  }

  onClickDeleteUser(userId: number): void {
    this.modalsService.showConfirmModal(() => {
      this.apiUsersService.deleteUser(userId).then(
        () => {
          this.alertsService.displayAlertSuccess('User deleted !');
          this.refreshUsersList();
        }, error => {
          this.alertsService.displayAlertError('Error deleting user : ' + error);
        }
      );
    });
  }

  private saveFilters() {
    localStorage.setItem(storageConfig.usersFilters, JSON.stringify(this.requestFilters));
  }
}
